// src/pages/PhotoDetails.tsx
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { 
  Box, 
  Paper, 
  Typography, 
  Grid, 
  Chip,
  Button,
  Divider,
  CircularProgress,
  Card,
  CardMedia,
  IconButton,
  Tooltip,
  Container,
  Alert
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Place as PlaceIcon,
  CalendarToday as CalendarTodayIcon,
  Image as ImageIcon,
  Tag as TagIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

import { PhotoComments } from '../components/Photos/PhotoComments';
import { useAuth } from '../contexts/AuthContext';

// Mapbox access token
mapboxgl.accessToken = 'pk.eyJ1IjoiMjA0NXB1Ymxpc2hpbmciLCJhIjoiY203bDdtZnltMDhwMDJub2Nsenc3ZW9neiJ9.BYHR9d2yYc1Yg9mPSdisVg';

// Photo interface
interface Photo {
  id: string;
  filename: string;
  url: string;
  thumbnailUrl: string;
  title: string;
  description: string;
  takenAt: Date;
  uploadedAt: Date;
  location?: {
    coordinates: [number, number]; // [longitude, latitude]
  };
  tags: string[];
  tripId: string;
}

const PhotoDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [photo, setPhoto] = useState<Photo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const { currentUser } = useAuth();
  const [isPhotoOwner, setIsPhotoOwner] = useState<boolean>(false);

  // Fetch photo details
  useEffect(() => {
    const fetchPhoto = async () => {
      if (!id) {
        setError('No photo ID provided');
        setLoading(false);
        return;
      }
      
      try {
        const photoDoc = await getDoc(doc(db, 'photos', id));
        
        if (!photoDoc.exists()) {
          setError('Photo not found');
          setLoading(false);
          return;
        }
        
        const data = photoDoc.data();
        const photoData: Photo = {
          id: photoDoc.id,
          filename: data.filename,
          url: data.url,
          thumbnailUrl: data.thumbnailUrl,
          title: data.title || data.filename,
          description: data.description || '',
          takenAt: data.takenAt.toDate(),
          uploadedAt: data.uploadedAt.toDate(),
          location: data.location,
          tags: data.tags || [],
          tripId: data.tripId || '',
        };
        
        // Check if current user is the photo owner
        setIsPhotoOwner(Boolean(currentUser) && data.uploadedBy === currentUser?.uid);
        
        setPhoto(photoData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching photo:', err);
        setError('Error loading photo details');
        setLoading(false);
      }
    };
    
    fetchPhoto();
  }, [id, currentUser]);

  // Initialize map when photo loads and has location
  useEffect(() => {
    if (!photo || !photo.location || !mapContainer.current) return;
    
    if (map.current) {
      // If map already exists, just update it
      map.current.setCenter(photo.location.coordinates);
      
      // Clear existing markers
      const markers = document.getElementsByClassName('mapboxgl-marker');
      while(markers[0]) {
        markers[0].remove();
      }
    } else {
      // Create new map
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: photo.location.coordinates,
        zoom: 12,
      });
      
      map.current.addControl(new mapboxgl.NavigationControl());
    }
    
    // Add marker
    const el = document.createElement('div');
    el.className = 'photo-marker';
    el.style.width = '20px';
    el.style.height = '20px';
    el.style.borderRadius = '50%';
    el.style.backgroundColor = '#e74c3c';
    el.style.border = '3px solid white';
    
    new mapboxgl.Marker(el)
      .setLngLat(photo.location.coordinates)
      .addTo(map.current);
    
  }, [photo]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !photo) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" color="error" gutterBottom>
          {error || 'Photo not found'}
        </Typography>
        <Button 
          startIcon={<ArrowBackIcon />} 
          onClick={() => navigate(-1)}
          variant="contained"
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            Back
          </Button>
          <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
            {photo.title}
          </Typography>
          {isPhotoOwner && (
            <Tooltip title="Edit Photo">
              <IconButton color="primary">
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        
        <Grid container spacing={3}>
          {/* Photo and Details */}
          <Grid item xs={12} md={8}>
            <Card elevation={2}>
              <CardMedia
                component="img"
                image={photo.url}
                alt={photo.title}
                sx={{ 
                  width: '100%', 
                  maxHeight: 600, 
                  objectFit: 'contain',
                  bgcolor: '#f5f5f5'
                }}
              />
            </Card>
            
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Details
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                  <CalendarTodayIcon color="action" sx={{ mr: 1 }} />
                  <Typography variant="body1">
                    Taken on: {photo.takenAt.toLocaleDateString()}
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                  <ImageIcon color="action" sx={{ mr: 1 }} />
                  <Typography variant="body1">
                    Filename: {photo.filename}
                  </Typography>
                </Grid>
              </Grid>
              
              {photo.description && (
                <Box sx={{ mt: 2, mb: 3 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    Description
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {photo.description}
                  </Typography>
                </Box>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TagIcon color="action" sx={{ mr: 1 }} />
                <Typography variant="subtitle1" sx={{ mr: 2 }}>
                  Tags:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {photo.tags.length > 0 ? (
                    photo.tags.map((tag) => (
                      <Chip 
                        key={tag} 
                        label={tag} 
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No tags
                    </Typography>
                  )}
                </Box>
              </Box>
            </Paper>
          </Grid>
          
          {/* Map and Location */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <PlaceIcon color="action" sx={{ mr: 1 }} />
                Location
              </Typography>
              
              {photo.location ? (
                <>
                  <Box 
                    ref={mapContainer} 
                    sx={{ 
                      height: 300,
                      borderRadius: 1,
                      overflow: 'hidden',
                      mb: 2 
                    }} 
                  />
                  <Typography variant="body2" color="text.secondary">
                    Coordinates: {photo.location.coordinates[1].toFixed(6)}, {photo.location.coordinates[0].toFixed(6)}
                  </Typography>
                </>
              ) : (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No location data available for this photo.
                  </Typography>
                </Box>
              )}
            </Paper>
            
            <Paper sx={{ p: 2, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Timeline
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={() => navigate('/timeline')}
                >
                  View in Timeline
                </Button>
              </Box>
            </Paper>
          </Grid>

          {/* Comments Section - Full Width */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <PhotoComments photoId={photo.id} tripId={photo.tripId} />
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default PhotoDetails;